import sortBy from "lodash/sortBy";

import { type FullStackPack, type StackPackConfiguration, StackPackInstanceState, StackPackStatus } from "~/models/dto";
import { errorStatus, getRequestStatusValue, loadingStatus, successStatus } from "~/tools/request-status";

import { orderAllStackPacksConfigurations } from "../../../../bottom_content/stackpacks/StackPacksUtils";
import { createReducer } from "../../../../generic/redux/createReducer";
import { pairActionTypeToReducer } from "../../../../generic/redux/TypedRedux";
import type { UpdateConfigurationStatus } from "../../pages/stackPacks/details/StackPacksDetailsActions";
import { STACK_PACKS_UPDATE_CONFIGURATION } from "../../pages/stackPacks/details/StackPacksDetailsActionTypes";

import type { SetStackPacks, SetStackPacksError } from "./StackPacksActions";
import { STACK_PACKS_FETCH, STACK_PACKS_SET, STACK_PACKS_SET_ERROR } from "./StackPacksActionTypes";
import { type StackPacksState, initialStackPacksState } from "./StackPacksState";

const fetchStackPacks = (state: StackPacksState): StackPacksState => ({
	...state,
	stackPacksStatus: loadingStatus(),
});

const setStackPacks = (state: StackPacksState, { stackPacks }: SetStackPacks): StackPacksState => {
	return {
		...state,
		stackPacksStatus: successStatus(
			sortBy(orderAllStackPacksConfigurations(stackPacks), ({ name, releaseStatus }) => [
				// We sort the value by first the releaseStatus, all the coming soon stack packs
				// should appear at the bottom of the list i.e. since sortBy sorts things in ascending order
				// we need to se ta higher value for coming soon ones
				releaseStatus === StackPackStatus.ComingSoon ? 1 : 0,
				// Then we sort by name
				name,
			]),
		),
	};
};

const setStackPacksError = (state: StackPacksState, { error }: SetStackPacksError): StackPacksState => ({
	...state,
	stackPacksStatus: errorStatus(error),
});

// This helper function looks at the state of a StackPack
// to see whether it can be swapped for its nextVersion
//
// This happens when either the last configuration is gone
// or if all the configurations have been updated to the same version
// as the nextVersion
const applyUpgrade = (stackPack: FullStackPack): FullStackPack => {
	const { configurations, nextVersion } = stackPack;
	if (!nextVersion) {
		return stackPack;
	}

	const areConfigurationsUpgraded = configurations.every(
		({ stackPackVersion }) => stackPackVersion === nextVersion.version,
	);
	if (!areConfigurationsUpgraded) {
		return stackPack;
	}

	return { ...nextVersion, configurations };
};

const updateStackPack =
	(stackPackName: string) => (stackPackConfiguration: StackPackConfiguration) => (stackPack: FullStackPack) => {
		if (stackPack.name !== stackPackName) {
			return stackPack;
		}

		const configurationIndex = stackPack.configurations.findIndex(({ id }) => id === stackPackConfiguration.id);
		const configurations = stackPack.configurations.slice();
		// Update the StackPack's configuration that came through the StatusUpdateStream
		configurations.splice(
			configurationIndex === -1 ? configurations.length : configurationIndex,
			1,
			...(stackPackConfiguration.status === StackPackInstanceState.NotInstalled ? [] : [stackPackConfiguration]),
		);

		return applyUpgrade({
			...stackPack,
			configurations,
		});
	};

const updateConfiguration = (state: StackPacksState, action: UpdateConfigurationStatus): StackPacksState => {
	const { stackPackName, stackPackConfiguration } = action.message;
	const stackPacksStatus = getRequestStatusValue(state.stackPacksStatus) ?? [];

	const fullStackPacksUpdater = (stackPacks: Array<FullStackPack>): Array<FullStackPack> =>
		stackPacks.map(updateStackPack(stackPackName)(stackPackConfiguration));
	const updateState = (packs: Array<FullStackPack>): StackPacksState => ({
		...state,
		stackPacksStatus: successStatus(orderAllStackPacksConfigurations(packs)),
	});

	const shouldUpdate = stackPacksStatus.some(({ name }) => name === stackPackName);

	return shouldUpdate ? updateState(fullStackPacksUpdater(stackPacksStatus)) : state;
};

export const StackPacksReducer = createReducer(initialStackPacksState, {
	...pairActionTypeToReducer(STACK_PACKS_FETCH, fetchStackPacks),
	...pairActionTypeToReducer(STACK_PACKS_SET, setStackPacks),
	...pairActionTypeToReducer(STACK_PACKS_SET_ERROR, setStackPacksError),

	...pairActionTypeToReducer(STACK_PACKS_UPDATE_CONFIGURATION, updateConfiguration),
});
